<!--
  FeaturesWidget — container for tab-based widgets (Popular Parlays, Player Props, etc.)
  To add a new tab, the following steps are required:

  1. CONFIG (app-config.js / env-config.js):
     Add an entry to window.ctsautoconf.FEATURES_TABS:
     { name: 'myTab', enabled: true, order: 3, showOnHome: true, showOnSports: true, showOnEvent: false }

  2. COMPONENT:
     Create a Vue component for the tab and import it here.
     Add it to componentMap: { 'myTab': 'MyTabComponent' }

  3. PROPS:
     Add an entry to propsMap defining which props the component receives (subset of available props):
     { 'myTab': ['displayMode', 'isFeatureTabs', 'eventId', ...] }
     Available props: displayMode, isFeatureTabs, isHomePage, competitions, sportType, isSportsPage, eventId, isPanelExpanded

  4. LOADING:
     The child component must commit the following in its mounted() hook:
       store.commit('featureTabsState/setIsLoading', true)  — before the API call
       store.commit('featureTabsState/setIsLoading', false) — in the finally block of the API call
     The panel opens automatically once isLoading becomes false.

  5. LABEL:
     Add a translation key in the getTabLabel() method.
-->
<template>
  <div class="features-widget-component" :class="[activeTab]">
    <div v-if="isCarouselMode" class="features-header" @click="collapseExpand()">
      <div class="tab-switcher">
        <div v-for="tab in availableFeatureTabs" :key="tab" class="tab-item" :class="{ 'active': activeTab === tab }" @click.stop="switchTab(tab)">
          <span class="icon" :class="[tab]"></span>
          <span class="feature-title">{{ getTabLabel(tab) }}</span>
        </div>
      </div>
      <div class="features-right-side">
        <circle-loader v-if="isLoading" />
        <div v-if="(mobileBigAndBelow && !isLoading) || !mobileBigAndBelow" class="features-arrow-icon">
        <i aria-hidden="true" class="v-icon material-icons theme--light">{{ expansionPanel === 0 ? 'keyboard_arrow_down' : 'keyboard_arrow_up' }}</i>
      </div>
      </div>
    </div>

    <v-expansion-panel class="features-holder" v-model="expansionPanel">
      <v-expansion-panel-content :disabled="!isCarouselMode">
        <v-card class="features-content">
          <transition name="fade" mode="out-in" appear>
            <keep-alive>
              <component v-bind:is="componentMap[activeTab]" v-bind="getPropsForTab(activeTab)" :key="componentMap[activeTab]"></component>
            </keep-alive>
          </transition>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </div>
</template>

<script>
// import config from '@/config'
import store from '@/store'
import CircleLoader from '@/components/common/CircleLoader'
import PlayerPropsWidget from '@/components/sports/bettingoffer/playerPropsWidget/PlayerPropsWidgetComponent.vue'
import PopularParlays from '@/components/sports/bettingoffer/popularParlays/PopularParlays'
import Screen from '@/components/mixins/Screen'

export default {
  name: 'FeaturesWidget',
  props: [
    'displayMode',
    'availableFeatureTabs',
    'isHomePage',
    'competitions',
    'sportType',
    'isSportsPage',
    'eventId'
  ],
  mixins: [
    Screen
  ],
  data () {
    return {
      activeTab: null,
      isCollapsedManually: false,
      componentMap: {
        'popularParlays': 'PopularParlays',
        'playerProps': 'PlayerPropsWidget'
      },
      propsMap: {
        'popularParlays': ['displayMode', 'isFeatureTabs', 'eventId'],
        'playerProps': ['displayMode', 'isFeatureTabs', 'isHomePage', 'competitions', 'sportType', 'isSportsPage', 'eventId', 'isPanelExpanded']
      }
    }
  },
  components: {
    CircleLoader,
    PlayerPropsWidget,
    PopularParlays
  },
  computed: {
    isCarouselMode () {
      return this.displayMode === 'carousel'
    },
    expansionPanel: {
      get () {
        return store.getters['featureTabsState/getExpansionPanel']
      },
      set (value) {
        store.commit('featureTabsState/setExpansionPanel', value)
      }
    },
    isLoading () {
      return store.getters['featureTabsState/getIsLoading']
    }
  },
  methods: {
    getPropsForTab (tab) {
      const allProps = {
        displayMode: this.displayMode,
        isFeatureTabs: true,
        isHomePage: this.isHomePage,
        competitions: this.competitions,
        sportType: this.sportType,
        isSportsPage: this.isSportsPage,
        eventId: this.eventId,
        isPanelExpanded: this.expansionPanel === 0
      }
      const allowed = this.propsMap[tab] || []
      return Object.fromEntries(Object.entries(allProps).filter(([k]) => allowed.includes(k)))
    },
    switchTab (tab) {
      if (this.activeTab !== tab) {
        setTimeout(() => {
          this.expansionPanel = 0
        }, 300)
      }
      this.activeTab = tab
      store.commit('featureTabsState/setActiveTab', this.activeTab)
    },
    getTabLabel (tab) {
      const labels = {
        popularParlays: this.$t('Feature.Tabs.Popular.Parlay.Title'),
        playerProps: this.$t('Feature.Tabs.Player.Props.Title'),
        flashBetting: this.$t('Feature.Tabs.Flash.Betting.Title')
      }
      return labels[tab] || tab
    },
    collapseExpand () {
      this.expansionPanel = this.expansionPanel === 0 ? null : 0
      this.isCollapsedManually = !this.expansionPanel
    }
  },
  watch: {
    isLoading (newVal, oldVal) {
      if (!newVal && !this.isCollapsedManually) {
        this.expansionPanel = 0
      }
    }
  },
  mounted () {
    this.activeTab = this.availableFeatureTabs[0]
    store.commit('featureTabsState/setActiveTab', this.activeTab)
    this.expansionPanel = null
  }
}
</script>

<style scoped lang="stylus">
@import "~@/css/config"
.url-sports:not(.url-event)
  .features-widget-component
    margin 0 5px 5px 0
    @media mobile-big-and-below
      margin 0
.features-widget-component
  .features-holder, .features-content
    background-color var(--popular-parlays-holder-bgr)
  .features-header
    display flex
    align-items center
    background var(--popular-parlays-holder-bgr)
    padding 0 8px
    height 50px
    cursor pointer
  >>>.v-expansion-panel__container
    .v-expansion-panel__header
      display none
  .features-right-side
    margin-left auto
    display flex
    align-items center
    pointer-events none
    >>> .loader
      margin 0
  .features-arrow-icon
    pointer-events none
    >>> .v-icon
      color var(--popular-parlays-holder-openclose-icon-color) !important
  .tab-switcher
    display flex
    align-items center
    background var(--feature-tabs-bgr)
    border-radius 50px
    gap 3px
    .tab-item
      display flex
      align-items center
      padding 6px 14px
      cursor pointer
      border-radius 50px
      color var(--feature-tabs-color)
      background transparent
      @media mobile-big-and-below
        padding 6px 10px
      .icon
        width 27px
        height 17px
        display inline-flex
        margin-right 10px
        @media mobile-big-and-below
          margin-right 5px
        &.playerProps
          height 20px
          background var(--player-props-widget-icon) 100% center no-repeat
        &.popularParlays
          background var(--pp-icon) 100% center no-repeat
      .feature-title
        font-size 15px
        font-family 'Roboto Condensed Bold'
        @media mobile-big-and-below
          font-size 13px

      &.active
        background var(--feature-tabs-bgr-active)
        color var(--feature-tabs-color-active)
        font-family 'Roboto Condensed Bold'
        .icon
          &.playerProps
            background var(--player-props-widget-icon-active) 100% center no-repeat

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease
  }

  .fade-enter, .fade-leave-to {
    opacity: 0
  }
</style>



// WEBPACK FOOTER //
// src/components/sports/bettingoffer/featureTabsWidget/featureTabsWidget.vue